import React from 'react';
import isNil from 'lodash/isNil';
import InsuranceChip from 'pages/Dashboard/components/InsuranceChips';
import ClaimTag from 'pages/Dashboard/pages/Billing/Charges/columns/ClaimTag';
import ClaimAcknowledgementStatus from 'pages/Dashboard/pages/Billing/Charges/ClaimAcknowledgementStatus';
import CreateSecondaryButton from 'pages/Dashboard/pages/Billing/Charges/CreateSecondaryButton';
import {
  ChargesClaimInfo,
  Claim,
} from 'pages/Dashboard/pages/Billing/types';
import CopyWrapper from 'core/components/CopyWrapper';
import ProvidersList from 'pages/Dashboard/pages/Billing/Charges/columns/ProvidersList';
import ProviderTooltip from 'pages/Dashboard/pages/Billing/Charges/columns/ProviderTooltip';
import compact from 'lodash/compact';
import Tooltip from 'core/components/Tooltip';
import Tag from 'core/components/Tag';
import ClaimNotes from 'pages/Dashboard/pages/Billing/Charges/columns/ClaimNotes';
import {
  getClaimInsuranceValidity,
} from 'pages/Dashboard/pages/Billing/Charges/utils';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import clsx from 'clsx';

type Props = {
  claim: ChargesClaimInfo;
  claimIndex?: number;
  patientId?: number;
  hideId?: boolean;
};

export default function ClaimRow({
  claim,
  claimIndex,
  patientId,
  hideId = false,
}: Props) {
  const { status: claimStatus, notes, isOutOfPocket, claimId, encounterId } = claim ?? {};
  const status = isOutOfPocket ? 'Out-of-pocket' : claimStatus ?? 'Not Started';
  const {
    isInvalid: hasInvalidInsurance,
    invalidTooltip: invalidInsuranceTooltip,
  } = getClaimInsuranceValidity(claim?.insurance);

  return (
    <div className="flex items-center gap-1 relative claim-row">
      <ClaimTag
        {...claim}
        claimIndex={claimIndex}
        isSecondary={!isNil(claim?.referencedPrimaryClaimId)}
        hasSecondary={!isNil(claim?.secondaryClaim)}
        status={status}
      />
      {!isNil(claim?.externalId) && !hideId && (
        <CopyWrapper
          text={claim?.externalId ?? ''}
          tooltip="Copy claim ID"
          className="copy-wrapper"
        />
      )}
      {!/out-of-pocket|not started/i.test(status ?? '') && (
        <ClaimNotes
          claimId={claimId ?? 0}
          encounterId={encounterId ?? 0}
          notes={notes ?? ''}
          className="claim-notes"
        />
      )}
      {!isNil(claim?.claimAcknowledgementStatus) && (
        <ClaimAcknowledgementStatus {...claim.claimAcknowledgementStatus} />
      )}
      <CreateSecondaryButton
        claim={claim as Claim}
        patientId={patientId ?? 0}
        tooltip="Create Secondary Claim"
      />
      <Tooltip title={(
        <div className="flex flex-col gap-2 p-1 font-semibold">
          {!/out-of-pocket/i.test(status ?? '') && (
            <div className="flex flex-col gap-1">
              <span className="text-gray-500">Insurance</span>
              <span className="text-gray-800">{claim?.insurance?.insuranceName ?? 'Not set'}</span>
            </div>
          )}
          <ProviderTooltip claim={claim} classes={{ row: '!px-0' }} />
        </div>
      )}
      >
        <span className="claim-info-short">
          <AdminPanelSettingsOutlinedIcon className={clsx('!w-[18px] !h-[18px]', {
            '!text-populate-gray-1': !hasInvalidInsurance,
            '!text-red-600': hasInvalidInsurance,
          })}
          />
        </span>
      </Tooltip>
      <div className="flex items-center gap-1 claim-info-details">
        {!/out-of-pocket/i.test(status ?? '') && (
        <span className={clsx('font-semibold', {
          'with-acknowledgement-status': !isNil(claim?.claimAcknowledgementStatus),
        })}
        >
          <InsuranceChip
            insuranceName={claim?.insurance?.insuranceName ?? 'Not Set'}
            className={clsx({ '!bg-red-100': hasInvalidInsurance })}
            invalid={hasInvalidInsurance}
            tooltip={hasInvalidInsurance ? { title: invalidInsuranceTooltip } : undefined}
          />
        </span>
        )}
        <ProvidersList
          tooltip={{
            title: (
              <ProviderTooltip claim={claim} />
            ),
          }}
          providers={compact([
            claim?.renderingProvider ?? {},
            claim?.billingProvider,
          ])}
          size="xs"
        />
      </div>
      {!isNil(claim?.crossoverCarrierPayer) && (
        <Tooltip
          title={(
            <div className="flex flex-col gap-2 w-48 p-1 font-semibold">
              <span className="text-gray-500">Crossover Payer</span>
              <span className="text-gray-800">{claim?.crossoverCarrierPayer ?? ''}</span>
            </div>
              )}
        >
          <div>
            <span className="crossover-payer-full">
              <Tag tagName="Crossover" className="!text-xs font-semibold" />
            </span>
            <span className="crossover-payer-short">
              <Tag tagName="C" className="!text-xs font-semibold" defaultColor="!bg-orange-100 !text-orange-700" />
            </span>
          </div>
        </Tooltip>
      )}
    </div>
  );
}
